/**
 * Side-effectful sign-in identity actions: link and unlink a provider.
 *
 * Kept apart from `authIdentities.js`, which stays pure so its guards can be
 * unit tested without the Firebase SDK. Everything here talks to Firebase Auth
 * and is exercised by hand.
 *
 * Two things every caller gets for free:
 *
 * 1. **Re-authentication.** Firebase refuses identity changes on a stale
 *    session (`auth/requires-recent-login`). Rather than making the user
 *    re-authenticate up front on every action — annoying, and usually
 *    unnecessary since sessions are often fresh — we attempt the action, and
 *    only on that specific error re-authenticate and retry ONCE. Re-auth needs
 *    an interactive provider, so an account whose only identity is email-link
 *    can't do it silently; there the error surfaces and the user signs in again.
 *
 * 2. **The address index refresh.** After a successful link or unlink the set
 *    of addresses that can sign this account in has changed, so
 *    `users/{uid}.emails[]` must be rebuilt — otherwise the person stays
 *    findable by an address they just removed, or unfindable by one they just
 *    added. Server-written, so we ask the callable to redo it.
 */
import {
  linkWithPopup, linkWithRedirect, unlink,
  reauthenticateWithPopup, sendSignInLinkToEmail,
  EmailAuthProvider, linkWithCredential,
} from "firebase/auth";
import { auth, googleProvider, appleProvider } from "../firebase";
import { mapAuthError, normalizeEmail, hasProvider, PROVIDER_GOOGLE, PROVIDER_APPLE } from "./authIdentities";
import { ensureAuthIdentities } from "./ensureAuthIdentities";
import { setEmailLinkIntent, takeEmailLinkIntent, peekEmailLinkIntent } from "./emailLinkIntent";
import { callFunction } from "./callFunction";
import { armLatePopupOutcome, supersedeLatePopupOutcome, isPopupClosedCode } from "./popupAuthOutcome";
import { signInMethodForProvider } from "./signInAnalytics";
import { logEvent, EVT } from "../analytics";

/** iOS standalone PWA: popups are unreliable, the redirect flow is the way. */
const isIOSStandalone = typeof window !== "undefined" && window.navigator.standalone === true;

function providerFor(providerId) {
  if (providerId === PROVIDER_GOOGLE) return googleProvider;
  if (providerId === PROVIDER_APPLE) return appleProvider;
  return null;
}

/** An interactive provider already on the account, usable for re-auth. */
function reauthProviderFor(user) {
  const ids = (user?.providerData || []).map(p => p.providerId);
  if (ids.includes(PROVIDER_GOOGLE)) return googleProvider;
  if (ids.includes(PROVIDER_APPLE)) return appleProvider;
  return null;
}

/** Rebuild the address index after the identity set changed. Best-effort. */
async function refreshEmailIndex() {
  const user = auth.currentUser;
  if (!user) return;
  try { await user.reload(); } catch { /* keep the local view */ }
  // Pass null so the comparison always mismatches and the callable definitely
  // runs — the whole point of calling it here is that the set just changed.
  await ensureAuthIdentities(auth.currentUser, null);
}

/**
 * Run `action`, re-authenticating and retrying once if Firebase demands a
 * fresh session. Returns `{ ok }` or `{ ok: false, errorKey, code }`, where a
 * null errorKey means "the user cancelled" and nothing should be shown. The
 * raw `code` is passed through so callers can branch on popup mechanics, and
 * `stage` says which popup it came from (`reauth` or the `action` itself).
 */
async function withReauth(action) {
  try {
    await action();
    return { ok: true };
  } catch (err) {
    if (err?.code !== "auth/requires-recent-login") {
      return { ok: false, errorKey: mapAuthError(err?.code), code: err?.code };
    }
    const provider = reauthProviderFor(auth.currentUser);
    if (!provider) {
      // Email-link-only account: no silent way to prove identity here.
      return { ok: false, errorKey: mapAuthError("auth/requires-recent-login"), code: err?.code };
    }
    supersedeLatePopupOutcome();
    try {
      await reauthenticateWithPopup(auth.currentUser, provider);
    } catch (reauthErr) {
      // A "closed" re-auth popup can still finish on a weak network
      // (popupAuthOutcome.js). Nothing to tell the user then — the action was
      // not retried, they start it again — but the late answer is recognised.
      armLatePopupOutcome(reauthErr?.code, {
        onSettled: () => logEvent(EVT.AUTH_POPUP_LATE, {
          kind: "reauth", outcome: "unknown",
          method: provider === googleProvider ? "google" : "apple",
        }),
      });
      return { ok: false, errorKey: mapAuthError(reauthErr?.code), code: reauthErr?.code, stage: "reauth" };
    }
    try {
      await action();
      return { ok: true };
    } catch (retryErr) {
      return { ok: false, errorKey: mapAuthError(retryErr?.code), code: retryErr?.code, stage: "action" };
    }
  }
}

/**
 * Popup mechanics failed — the browser refused to open or drive it. Only these
 * justify the redirect fallback; a user-cancelled popup or a real credential
 * conflict must surface as-is.
 */
const POPUP_MECHANICS_FAILURE = [
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
  "auth/web-storage-unsupported",
];

/**
 * Link a federated provider (Google / Apple) to the current account.
 *
 * **Popup on every platform, redirect only as a genuine fallback.** This
 * mirrors the sign-in flow, and for the same hard-won reason: in an iOS
 * standalone PWA the redirect round-trip can open in Safari, and the PWA then
 * loses the persisted pending-redirect state — the operation completes
 * nowhere the app can see, so the user gets no link and no error. Going
 * redirect-first on iOS reproduced exactly that. The popup, by contrast,
 * returns cleanly into the PWA and closes over postMessage.
 *
 * The fallback therefore triggers only when the popup itself could not be
 * driven (blocked / unsupported), never on a user cancel and never on a real
 * credential conflict — those must surface. See `docs/spec/19-infra.md`,
 * "Authentication".
 *
 * **A "closed" popup is not always the end.** On a weak network the SDK can
 * declare the link popup closed while its request is still running, and the
 * provider then gets linked anyway (popupAuthOutcome.js). `onLateResult`
 * receives that late outcome — `{ ok: true }` or `{ ok: false, errorKey }` —
 * after the address index was refreshed, so the caller can treat it exactly
 * like an immediate result.
 */
export async function linkProvider(providerId, { onLateResult } = {}) {
  const provider = providerFor(providerId);
  const user = auth.currentUser;
  if (!provider || !user) return { ok: false, errorKey: "security.errGeneric" };

  supersedeLatePopupOutcome();
  const res = await withReauth(() => linkWithPopup(auth.currentUser, provider));
  if (res.ok) {
    await refreshEmailIndex();
    return res;
  }

  if (isIOSStandalone && POPUP_MECHANICS_FAILURE.includes(res.code)) {
    try {
      await linkWithRedirect(auth.currentUser, provider);
      return { ok: false, redirecting: true };
    } catch (err) {
      return { ok: false, errorKey: mapAuthError(err?.code) };
    }
  }

  // withReauth already armed the window when it was the re-auth popup.
  if (isPopupClosedCode(res.code) && res.stage !== "reauth") {
    armLatePopupOutcome(res.code, {
      succeeded: () => hasProvider(auth.currentUser, providerId),
      onSettled: (ok) => {
        logEvent(EVT.AUTH_POPUP_LATE, {
          kind: "link", outcome: ok ? "success" : "failure",
          method: signInMethodForProvider(providerId),
        });
        reportLateLink(ok, onLateResult);
      },
    });
  }

  return res;
}

/** Hand a late link outcome to the caller, the address index rebuilt first. */
async function reportLateLink(ok, onLateResult) {
  try {
    if (ok) await refreshEmailIndex();
  } catch {
    // The bootstrap rebuilds the index on the next sign-in anyway.
  }
  onLateResult?.(ok ? { ok: true } : { ok: false, errorKey: "security.errGeneric" });
}

/**
 * Unlink a provider from the current account.
 *
 * The caller is responsible for the last-identity guard (`canUnlink`) — this
 * only performs the removal. Firebase would also reject removing the final
 * provider, but the UI must never offer it in the first place.
 */
export async function unlinkProvider(providerId) {
  const user = auth.currentUser;
  if (!user) return { ok: false, errorKey: "security.errGeneric" };

  const res = await withReauth(() => unlink(auth.currentUser, providerId));
  if (res.ok) await refreshEmailIndex();
  return res;
}

/* ── Email-link setup ──────────────────────────────────────────────────── */

/** Current URL with Firebase's auth params stripped — where the link returns. */
function returnUrl() {
  const url = new URL(window.location.href);
  ["apiKey", "oobCode", "mode", "continueUrl", "lang"].forEach(p => url.searchParams.delete(p));
  return url.origin + url.pathname + url.search + url.hash;
}

/**
 * Begin adding email-link sign-in to the current account.
 *
 * Sends a sign-in link and records WHY it was sent. The link itself is
 * indistinguishable from an ordinary sign-in link, so without that record the
 * boot handler would just sign the user in again and nothing would be linked —
 * see `emailLinkIntent.js`. The link is completed by `completeEmailLinkSetup`
 * when the app reloads.
 *
 * The address may differ from the account's current one. Unlike a federated
 * identity — which is keyed by provider UID and merely carries an address as
 * metadata — an email-link identity IS its address, so Firebase resolves it
 * through the account-level email. Linking a different address therefore also
 * moves the account's primary address, and the UI says so before sending.
 */
export async function startEmailLinkSetup(rawEmail) {
  const user = auth.currentUser;
  const email = normalizeEmail(rawEmail);
  if (!user || !email) return { ok: false, errorKey: "security.errGeneric" };

  try {
    await sendSignInLinkToEmail(auth, email, { url: returnUrl(), handleCodeInApp: true });
    setEmailLinkIntent({ mode: "link", email, uid: user.uid });
    return { ok: true, email };
  } catch (err) {
    return { ok: false, errorKey: mapAuthError(err?.code) };
  }
}

/**
 * The address a pending email-link setup is waiting on, or null.
 *
 * Returns the address rather than a boolean so the UI can NAME it — "we sent a
 * link to your address" is useless when the user is wondering which inbox to
 * check.
 */
export function getPendingEmailLinkSetup(uid) {
  const intent = peekEmailLinkIntent();
  if (!intent || intent.mode !== "link") return null;
  if (uid && intent.uid !== uid) return null;
  return intent.email;
}

/** Abandon a pending setup so a different address can be tried. */
export { clearEmailLinkIntent as cancelEmailLinkSetup } from "./emailLinkIntent";

/**
 * Finish an email-link setup after the link was opened.
 *
 * Called by both boot handlers BEFORE they fall back to signing in. Returns
 * `{ handled: false }` when this link is an ordinary sign-in, letting the
 * caller proceed exactly as before.
 *
 * The intent is consumed on read, so a link opened twice — or opened while a
 * different account is signed in — can never re-run this.
 */
export async function completeEmailLinkSetup(link) {
  if (!peekEmailLinkIntent()) return { handled: false };

  // The effect runs before auth state is restored, so currentUser may still be
  // null here; wait for the SDK to settle before deciding.
  try { await auth.authStateReady(); } catch { /* older SDK — fall through */ }

  const intent = takeEmailLinkIntent();
  if (!intent || intent.mode !== "link") return { handled: false };

  const user = auth.currentUser;
  // A link belonging to another account (or opened while signed out) must not
  // attach an address to whoever happens to be signed in — let it sign in.
  if (!user || user.uid !== intent.uid) return { handled: false };

  try {
    await linkWithCredential(user, EmailAuthProvider.credentialWithLink(intent.email, link));
    await refreshEmailIndex();
    return { handled: true, ok: true };
  } catch (err) {
    return { handled: true, ok: false, errorKey: mapAuthError(err?.code) };
  }
}

/* ── The account address ───────────────────────────────────────────────── */

/**
 * Make one of the account's own sign-in addresses the account address.
 *
 * No confirmation email: the address is already asserted by a linked provider,
 * so control is proven. The server re-checks that against the auth record —
 * see functions/src/setPrimaryEmail.js — and sets it via the Admin SDK, which
 * is the only way to move the account address without a delivery round-trip.
 */
export async function setPrimaryEmail(rawEmail) {
  const email = normalizeEmail(rawEmail);
  if (!auth.currentUser || !email) return { ok: false, errorKey: "security.errGeneric" };

  try {
    await callFunction("setPrimaryEmail", { email });
    // The local user object still carries the old address until it refreshes.
    try { await auth.currentUser.reload(); } catch { /* keep the local view */ }
    return { ok: true, email };
  } catch (err) {
    return { ok: false, errorKey: mapAuthError(err?.code) };
  }
}

/*
 * There is deliberately NO free-text `changeEmail` / `verifyBeforeUpdateEmail`
 * path here.
 *
 * The account address is not an independently editable value: it is the
 * address of whichever sign-in method is primary. Switching it therefore means
 * `setPrimaryEmail` above, and a genuinely NEW address enters the account only
 * by adding an email-link sign-in for it (`startEmailLinkSetup`), which proves
 * control by delivery just as a verification mail would.
 *
 * The earlier free-text field plus its pending-confirmation bookkeeping was a
 * second control moving the same value, which is what made this section
 * incomprehensible: adding a sign-in method silently changed where the user's
 * email went.
 */
